/* @ts-ignore */
import React, { useMemo } from 'react';
import Image from 'next/image';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleExclamation,
  faCircleCheck,
} from '@fortawesome/free-solid-svg-icons';

import { Button } from '~/components/ui';
import { useEmailSubscribe } from '~/lib/util/hooks';
import { UseEmailSubscribeProps } from '~/lib/util/hooks/emailSubscribe';
import useUser from '~/lib/user';
import BgMask from './assets/bg-mask.svg';
import Streak from './assets/streak.svg';
import { GA4FormContentValue } from '~/lib/util/constants';

interface ComponentNameProps {
  componentName?: string;
  content?: string;
}

const EmailErrorMessage = ({
  errorMessage,
}: {
  errorMessage: string;
}): React.ReactElement => {
  if (!errorMessage) return <></>;

  return (
    <div className="mt-2">
      <FontAwesomeIcon
        icon={faCircleExclamation}
        height="16"
        className="mr-2"
      />
      <span className="text-sm">{errorMessage}</span>
    </div>
  );
};

const EmailCapture = ({
  componentName,
  content,
}: ComponentNameProps): React.ReactElement => {
  const emailSubscribeProps: UseEmailSubscribeProps = {
    customFormData: { component: componentName, content },
  };

  const {
    hasEmailError,
    isEmailSubmitted,
    isEmailValid,
    onChangeEmail,
    submitEmail,
  } = useEmailSubscribe(emailSubscribeProps);

  function onSubmit(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    submitEmail();
  }

  const errorMessage = useMemo(() => {
    if (!isEmailValid) {
      return 'A valid email address is required to get 10% off.';
    }

    if (hasEmailError) {
      return hasEmailError;
    }

    return '';
  }, [hasEmailError, isEmailValid]);

  if (isEmailSubmitted) {
    return (
      <div className="mt-4 flex items-center rounded bg-dollar-light-40 px-4 py-3 text-midnight">
        <FontAwesomeIcon
          icon={faCircleCheck}
          height="18"
          className="mr-2 text-lg"
        />
        <span className="text-sm">You&apos;re in! Thanks for signing up.</span>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl">
      <label htmlFor="email" className="mb-2 block font-header font-semibold">
        Email
      </label>
      <div className="flex w-full max-w-2xl gap-2 max-sm:flex-col">
        <div
          className={clsx(
            'grow rounded border-2 max-sm:mb-4',
            errorMessage ? 'border-brick' : ' border-transparent',
          )}
        >
          <input
            className={clsx(
              'w-full rounded border-2 px-4 py-2 text-midnight',
              errorMessage
                ? 'rounded-none border-brick focus:border-brick focus:outline-none'
                : ' border-transparent',
            )}
            onChange={onChangeEmail}
            id="email"
          />
        </div>

        <Button onClick={onSubmit}>Get 10% off</Button>
      </div>
      <EmailErrorMessage errorMessage={errorMessage} />
    </div>
  );
};

export const BlueEmailSubscribe = ({ componentName }: ComponentNameProps) => {
  const { user } = useUser();

  // Hide for logged in users
  if (user?.email) return <></>;

  return (
    <div className="max-sm:border-b max-sm:border-b-sundog max-sm:pb-4">
      <div
        className="relative flex flex-col items-center justify-center gap-4 rounded border-b bg-bolt-light-20 bg-x-120 bg-no-repeat px-4 py-10 text-white sm:bg-x-90 sm:p-10 sm:shadow lg:flex-row lg:gap-x-8 lg:p-8"
        style={{
          backgroundImage: `url(${BgMask.src})`,
          backgroundPosition: '0 100%',
        }}
      >
        <div className="relative flex w-full items-center justify-center gap-x-20 gap-y-8 max-sm:flex-col sm:max-w-6xl">
          <div className="relative flex w-full flex-col items-center justify-center gap-y-4">
            <div className="flex flex-col items-center">
              <p className="mb-1 font-header text-[56px] font-extrabold leading-[56px]">
                Want 10% off
              </p>
              <div className="relative h-[10px] w-full">
                <Image src={Streak.src} alt="streak" fill />
              </div>
              <p className="font-header text-2xl leading-8">
                your first purchase?
              </p>
            </div>
            <EmailCapture
              componentName={componentName}
              content={GA4FormContentValue.alternativeTo10Off}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
